import { createSlice } from "@reduxjs/toolkit";
import {
  getMediaMgt,
  getMediaFile,
  createOrUpdateMediaMgt,
  deleteMediaMgt,
  getSubActiveTabId,
  getMediaExpandCollapseInfo,
  getResetMediaMgt,
  getMediaShowEdit,
  getMediaHideEdit,
} from "../services/mediaMgt";

export interface MediaItem {
  mediaId: string;
  highLight?: boolean;
  [key: string]: unknown;
}

export interface MediaMgtDataContainer {
  data: MediaItem[];
  [key: string]: unknown;
}

interface MediaMgtState {
  mediaMgtStatus: string;
  activeSubTabId: string;
  mediaMgtFileStatus: string;
  mediaExpandCollapseInfo: unknown | null;
  isMMPending: boolean;
  isMMSave: boolean;
  isMMDelete: boolean;
  isMMError: boolean;
  mediaContent: string | null;
  mediaMgtData: MediaMgtDataContainer | MediaItem[];
  isEditOpen: boolean;
  editData: unknown | null;
  languageId: string;
}

const initialState: MediaMgtState = {
  mediaMgtStatus: "idle",
  activeSubTabId: "subTab1",
  mediaMgtFileStatus: "idle",
  mediaExpandCollapseInfo: null,
  isMMPending: false,
  isMMSave: false,
  isMMDelete: false,
  isMMError: false,
  mediaContent: null,
  mediaMgtData: [],
  isEditOpen: false,
  editData: null,
  languageId: "",
};

const processPayload = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const clonedPayload = { ...payload };
  delete clonedPayload["__typename"];
  delete clonedPayload["__operation"];
  return clonedPayload;
};

const mediaMgtSlice = createSlice({
  name: "mediaMgt",
  initialState,
  reducers: {
    updateLanguageId: (state, { payload }: { payload: string }) => {
      state.languageId = payload;
    },

    createMMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      if (Array.isArray(state.mediaMgtData)) return;
      const processedPayload = processPayload(payload);
      (state.mediaMgtData as MediaMgtDataContainer).data.push({
        ...processedPayload,
        highLight: true,
      } as MediaItem);
    },
    updateMMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      if (Array.isArray(state.mediaMgtData)) return;
      const processedPayload = processPayload(payload);
      const container = state.mediaMgtData as MediaMgtDataContainer;
      container.data = container.data.map((item) => {
        if (item.mediaId === processedPayload.mediaId) {
          return { ...processedPayload, highLight: true } as MediaItem;
        } else {
          return { ...item, highLight: false };
        }
      });
    },
    deleteMMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      if (Array.isArray(state.mediaMgtData)) return;
      const processedPayload = processPayload(payload);
      const idToDelete = processedPayload.mediaId;
      const container = state.mediaMgtData as MediaMgtDataContainer;
      container.data = container.data.filter(
        (item) => item.mediaId !== idToDelete,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMediaMgt.pending, (state) => {
        state.mediaMgtStatus = "pending";
        state.isMMPending = false;
        state.isMMSave = false;
        state.isMMDelete = false;
        state.isMMError = false;
        state.mediaMgtData = [];
      })
      .addCase(getMediaMgt.fulfilled, (state, action) => {
        const payload = action.payload as { data: MediaMgtDataContainer };
        state.mediaMgtStatus = "success";
        state.mediaMgtData = payload.data;
      })
      .addCase(getMediaMgt.rejected, (state) => {
        state.mediaMgtStatus = "rejected";
        state.isMMPending = false;
        state.isMMSave = false;
        state.isMMDelete = false;
        state.isMMError = true;
        state.mediaMgtData = [];
      })
      .addCase(getMediaFile.pending, (state) => {
        state.mediaMgtFileStatus = "pending";
        state.mediaContent = null;
      })
      .addCase(getMediaFile.fulfilled, (state, action) => {
        const payload = action.payload as { data: { mediaContent: string } };
        state.mediaMgtFileStatus = "success";
        state.mediaContent = payload.data.mediaContent;
      })
      .addCase(getMediaFile.rejected, (state) => {
        state.mediaMgtFileStatus = "rejected";
        state.mediaContent = null;
      })
      .addCase(createOrUpdateMediaMgt.pending, (state) => {
        state.isMMPending = true;
        state.isMMSave = false;
        state.isMMError = false;
        state.mediaMgtStatus = "pending";
      })
      .addCase(createOrUpdateMediaMgt.fulfilled, (state, action) => {
        const payload = action.payload as { data: MediaMgtDataContainer };
        state.mediaMgtStatus = "created";
        state.isMMSave = true;
        state.isMMError = false;
        state.isMMPending = false;
        state.mediaMgtData = payload.data;
      })
      .addCase(createOrUpdateMediaMgt.rejected, (state) => {
        state.mediaMgtStatus = "rejected";
        state.isMMSave = false;
        state.isMMPending = false;
        state.isMMError = true;
      })
      .addCase(deleteMediaMgt.pending, (state) => {
        state.isMMPending = true;
        state.isMMSave = false;
        state.isMMError = false;
        state.mediaMgtStatus = "pending";
      })
      .addCase(deleteMediaMgt.fulfilled, (state, action) => {
        const payload = action.payload as { data: MediaMgtDataContainer };
        state.mediaMgtStatus = "deleted";
        state.mediaMgtData = payload.data;
        state.isMMPending = false;
        state.isMMDelete = true;
        state.isMMError = false;
      })
      .addCase(deleteMediaMgt.rejected, (state) => {
        state.mediaMgtStatus = "rejected";
        state.isMMPending = false;
        state.isMMDelete = false;
        state.isMMError = true;
      })
      .addCase(getSubActiveTabId, (state, action) => {
        state.activeSubTabId = action.payload as unknown as string;
      })
      .addCase(getMediaExpandCollapseInfo, (state, action) => {
        state.mediaExpandCollapseInfo = action.payload as unknown;
      })
      .addCase(getResetMediaMgt, (state) => {
        state.mediaMgtStatus = "idle";
        state.mediaMgtFileStatus = "idle";
        state.isMMPending = false;
        state.isMMSave = false;
        state.isMMDelete = false;
        state.isMMError = false;
        state.mediaExpandCollapseInfo = null;
      })
      .addCase(getMediaShowEdit, (state, action) => {
        state.isEditOpen = true;
        state.editData =
          (action.payload as Record<string, unknown>)?.data ?? null;
      })
      .addCase(getMediaHideEdit, (state) => {
        state.isEditOpen = false;
        state.editData = null;
      });
  },
});

export const { updateMMData, createMMData, deleteMMData, updateLanguageId } =
  mediaMgtSlice.actions;
export default mediaMgtSlice.reducer;
